import React, { useState, useEffect } from 'react';
import { User, CreateUserData } from '../../types';
import { api } from '../../services/api';
import CreateUserModal from './components/CreateUserModal';
import EditUserModal from './components/EditUserModal';
import './Users.css';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Efecto para filtrar usuarios basado en la búsqueda y filtro de rol
  useEffect(() => {
    let filtered = users;

    // Filtrar por término de búsqueda
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.first_name.toLowerCase().includes(searchLower) ||
        user.last_name.toLowerCase().includes(searchLower) ||
        user.username.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchLower)
      );
    }

    // Filtrar por rol
    if (roleFilter !== 'all') {
      if (roleFilter === 'superuser') {
        filtered = filtered.filter(user => user.is_superuser);
      } else {
        filtered = filtered.filter(user => user.role === roleFilter && !user.is_superuser);
      }
    }

    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/');
      if (response.data.success) {
        setUsers(response.data.users);
      } else {
        setError('Error al cargar usuarios');
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError(error.response?.data?.error || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData: CreateUserData) => {
    try {
      const response = await api.post('/users/', userData);
      if (response.data.success) {
        fetchUsers();
        setShowCreateModal(false);
        setError('');
      } else {
        setError(response.data.error || 'Error al crear usuario');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      setError(error.response?.data?.error || 'Error al crear usuario');
    }
  };

  const handleEditUser = async (userId: number, userData: Partial<User>) => {
    try {
      const response = await api.put(`/users/${userId}/`, userData);
      if (response.data.success) {
        fetchUsers();
        setEditingUser(null);
        setError('');
      } else {
        setError(response.data.error || 'Error al actualizar usuario');
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      setError(error.response?.data?.error || 'Error al actualizar usuario');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
      try {
        const response = await api.delete(`/users/${userId}/`);
        if (response.data.success) {
          fetchUsers();
          setError('');
        } else {
          setError(response.data.error || 'Error al eliminar usuario');
        }
      } catch (error: any) {
        console.error('Error deleting user:', error);
        setError(error.response?.data?.error || 'Error al eliminar usuario');
      }
    }
  };

  const getRoleBadgeClass = (role: string) => {
    return role === 'admin' ? 'role-badge admin' : 'role-badge employee';
  };

  const getRoleText = (role: string) => {
    return role === 'admin' ? 'Administrador' : 'Empleado';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Cargando usuarios...</div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-header">
        <h1>Gestión de Usuarios</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          + Crear Usuario
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Controles de filtrado */}
      <div className="user-filters">
        <div className="filter-controls">
          {/* Buscador */}
          <div className="search-container">
            <input
              type="text"
              placeholder="Buscar por nombre, email o usuario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="search-clear"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>

          {/* Filtro por rol */}
          <div className="filter-select-container">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Todos los roles</option>
              <option value="admin">Administradores</option>
              <option value="employee">Empleados</option>
              <option value="superuser">Superusers</option>
            </select>
          </div>
        </div>

        {/* Indicador de filtros activos */}
        {(searchTerm.trim() !== '' || roleFilter !== 'all') && (
          <div className="active-filters">
            <div className="filter-info">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707v4.586l-4-4V9.414a1 1 0 00-.293-.707L3.293 2.707A1 1 0 013 2V4z" />
              </svg>
              <span>
                Mostrando {filteredUsers.length} de {users.length} usuario{users.length !== 1 ? 's' : ''}
                {searchTerm.trim() !== '' && ` · Búsqueda: "${searchTerm}"`}
                {roleFilter !== 'all' && ` · Rol: ${
                  roleFilter === 'admin' ? 'Administradores' :
                  roleFilter === 'employee' ? 'Empleados' :
                  roleFilter === 'superuser' ? 'Superusers' : roleFilter
                }`}
              </span>
            </div>
            <button
              onClick={() => {
                setSearchTerm('');
                setRoleFilter('all');
              }}
              className="clear-filters-btn"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      <div className="users-grid">
        {users.length === 0 ? (
          <div className="no-users">
            <p>No hay usuarios registrados</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="no-users">
            <div className="empty-state-content">
              <svg className="empty-state-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '64px', height: '64px', color: '#d1d5db', margin: '0 auto 16px' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#111827', marginBottom: '8px' }}>
                No se encontraron usuarios
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '16px' }}>
                {searchTerm.trim() !== '' || roleFilter !== 'all' 
                  ? 'No hay usuarios que coincidan con los filtros aplicados'
                  : 'No hay usuarios registrados'
                }
              </p>
              {(searchTerm.trim() !== '' || roleFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setRoleFilter('all');
                  }}
                  className="btn btn-primary"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
        ) : (
          filteredUsers.map((user) => (
            <div key={user.id} className="user-card">
              <div className="user-info">
                <div className="user-avatar">
                  {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                </div>
                <div className="user-details">
                  <h3>{user.first_name} {user.last_name}</h3>
                  <p className="username">@{user.username}</p>
                  <p className="email">{user.email}</p>
                  <div className="user-badges">
                    <span className={getRoleBadgeClass(user.role)}>
                      {getRoleText(user.role)}
                    </span>
                    {user.is_superuser && (
                      <span className="role-badge superuser">
                        Superuser
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="user-actions">
                {user.is_superuser ? (
                  <div className="superuser-protection">
                    <span className="protection-note">
                      Los superusers están protegidos y no pueden ser modificados
                    </span>
                  </div>
                ) : (
                  <>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setEditingUser(user)}
                    >
                      Editar
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      Eliminar
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateUser}
          error={error}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleEditUser}
          error={error}
        />
      )}
    </div>
  );
};

export default UserManagement;